import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import aiAutomationApi from '../../utils/aiAutomationApi';

export default function AdminDashboard() {
  const { settings } = useSettings();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const primaryColor = settings?.theme?.colors?.primary || '#3B5BDB';

  const [summary, setSummary] = useState(null);
  const [llmConfigs, setLlmConfigs] = useState([]);
  const [keyStatus, setKeyStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usageRes, configRes, keyRes] = await Promise.allSettled([
        aiAutomationApi.get('/openai/usage/summary?start_date=' + new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0] + '&end_date=' + new Date().toISOString().split('T')[0]),
        aiAutomationApi.get('/admin/ai-config'),
        aiAutomationApi.get('/openai/check'),
      ]);
      if (usageRes.status === 'fulfilled') setSummary(usageRes.value);
      if (configRes.status === 'fulfilled') setLlmConfigs(configRes.value?.configs || []);
      if (keyRes.status === 'fulfilled') setKeyStatus(keyRes.value);
    } catch (err) {
      console.error('Admin dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const providerLabel = (p) => (p || 'openai').charAt(0).toUpperCase() + (p || 'openai').slice(1);
  const isHealthy = keyStatus?.status === 'success';

  const cards = [
    {
      title: 'API Calls (7d)',
      value: summary?.totals?.calls?.toLocaleString() || '—',
      sub: `${summary?.totals?.errors || 0} errors`,
      color: primaryColor,
    },
    {
      title: 'Tokens Used (7d)',
      value: summary?.totals?.tokens?.toLocaleString() || '—',
      sub: `$${(summary?.totals?.cost || 0).toFixed(2)} estimated cost`,
      color: '#20C997',
    },
    {
      title: 'API Status',
      value: isHealthy ? 'Healthy' : (keyStatus?.error_code === 'quota_exceeded' ? 'Quota Exceeded' : 'Issue'),
      sub: providerLabel(keyStatus?.provider),
      color: isHealthy ? '#22C55E' : '#EF4444',
    },
    {
      title: 'Active Providers',
      value: summary?.by_provider?.length || '—',
      sub: (summary?.by_provider || []).map(p => providerLabel(p.provider)).join(', ') || 'Loading...',
      color: '#8B5CF6',
    },
  ];

  return (
    <div className="px-6 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Overview of AI configuration and usage across the platform.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 dark:border-gray-600" style={{ borderTopColor: primaryColor }} />
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {cards.map((card, i) => (
              <div key={i} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5">
                <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">{card.title}</p>
                <p className="text-2xl font-bold" style={{ color: card.color }}>{card.value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{card.sub}</p>
              </div>
            ))}
          </div>

          {/* LLM Configuration Overview */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">LLM Configuration</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Current provider and model for each tier</p>
              </div>
              <button
                onClick={() => navigate('/admin/ai-config')}
                className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Configure
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {llmConfigs.map((cfg) => (
                <div key={cfg.tier} className="p-4 rounded-xl border border-gray-100 dark:border-gray-700" style={{ backgroundColor: isDark ? '#1F2937' : '#F9FAFB' }}>
                  <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: primaryColor }}>{cfg.tier}</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" title={cfg.model}>{cfg.model}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{providerLabel(cfg.provider)}</p>
                  <div className="mt-2 flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${cfg.has_api_key ? 'bg-green-400' : 'bg-red-400'}`} />
                    <span className="text-xs text-gray-400">{cfg.has_api_key ? 'Key configured' : 'No key'}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/admin/usage')}
              className="flex items-center gap-4 p-5 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                <svg className="w-5 h-5" style={{ color: primaryColor }} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Usage & Billing</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">View detailed API usage, costs, and billing cycles</p>
              </div>
            </button>
            <button
              onClick={() => navigate('/admin/ai-config')}
              className="flex items-center gap-4 p-5 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                <svg className="w-5 h-5" style={{ color: primaryColor }} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">AI Configuration</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Configure LLM providers, models, and API keys per tier</p>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
